import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { awaitSessionBootstrap } from '../session/hydrateSessionCachesFromBootstrap';
import {
  fetchMyOrganizationEntitlements,
  getOrganizationEntitlementsCacheIfFresh,
  isOrganizationEntitlementsCacheFresh,
  primeOrganizationEntitlementsCache,
} from '../feature-flags/planEntitlementsService';
import { clearPlanIntentIfSubscribed } from '../feature-flags/planIntentService';
import { subscribeSessionCacheBroadcast } from '../../shell/session/sessionCacheBroadcast';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import { getOfflineAccountSnapshot, saveEntitlementsOffline } from '../../shell/session/offlineAccountCache';
import { clearSessionOrganizationCache, primeSessionOrganizationFromEntitlements } from '../session/sessionOrganization';
import { isBillingEnabled } from '../billing/isBillingEnabled';
import { verifyMpSubscription } from '../billing/verifyMpSubscription';

type RefreshEntitlementsOptions = {
  force?: boolean;
};

type PlanEntitlementsContextValue = {
  entitlements: PlanEntitlementsRow | null;
  loading: boolean;
  refresh: (options?: RefreshEntitlementsOptions) => Promise<void>;
  isStale: boolean;
  onboardingCompleteFlag: boolean;
};

const PlanEntitlementsContext = createContext<PlanEntitlementsContextValue | null>(null);

export function PlanEntitlementsProvider({ children }: { children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session);
  const [entitlements, setEntitlements] = useState<PlanEntitlementsRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [onboardingCompleteFlag, setOnboardingCompleteFlag] = useState(false);
  const initialFetchDoneRef = useRef(false);
  const entitlementsRef = useRef(entitlements);
  entitlementsRef.current = entitlements;

  useEffect(() => {
    if (!session?.user?.id) {
      initialFetchDoneRef.current = false;
    }
  }, [session?.user?.id]);

  useEffect(() => {
    return subscribeSessionCacheBroadcast((message) => {
      if (message.type !== 'entitlements') return;
      primeOrganizationEntitlementsCache(message.payload);
      setEntitlements(message.payload);
      setIsStale(false);
      initialFetchDoneRef.current = true;
      setLoading(false);
    });
  }, []);

  const refresh = useCallback(async (options?: RefreshEntitlementsOptions) => {
    const currentSession = useAuthStore.getState().session;
    if (!currentSession) {
      clearSessionOrganizationCache();
      setEntitlements(null);
      setLoading(false);
      setIsStale(false);
      setOnboardingCompleteFlag(false);
      initialFetchDoneRef.current = false;
      return;
    }
    const uid = currentSession.user.id;
    const force = options?.force === true;
    if (!force && initialFetchDoneRef.current && isOrganizationEntitlementsCacheFresh()) {
      return;
    }
    if (!initialFetchDoneRef.current) {
      setLoading(true);
    }
    let cachePrimed = false;
    // Offline snap en paralelo: no frenar el RPC esperando migraciones SQLite.
    const offlinePrime = (async () => {
      try {
        const snap = await getOfflineAccountSnapshot(uid);
        if (useAuthStore.getState().session?.user?.id !== uid) return;
        if (snap?.onboardingComplete) {
          setOnboardingCompleteFlag(true);
        }
        if (snap?.requiresOwnerOnboarding === false) {
          setOnboardingCompleteFlag(true);
        }
        if (snap?.entitlements) {
          setEntitlements(snap.entitlements);
          primeSessionOrganizationFromEntitlements(uid, snap.entitlements);
          setIsStale(true);
          cachePrimed = true;
        }
        if (snap?.onboardingComplete || snap?.entitlements) {
          setLoading(false);
        }
      } catch (e) {
        if (!isDatabaseUnavailableError(e)) {
          console.warn('[PlanEntitlementsProvider] cache read', e);
        }
      }
    })();

    try {
      await awaitSessionBootstrap(uid);
      void offlinePrime;
      let row: PlanEntitlementsRow | null;
      if (!force && isOrganizationEntitlementsCacheFresh()) {
        row = getOrganizationEntitlementsCacheIfFresh() ?? null;
      } else {
        row = await fetchMyOrganizationEntitlements({ force });
      }
      if (useAuthStore.getState().session?.user?.id !== uid) return;
      initialFetchDoneRef.current = true;
      if (row && !isBillingEnabled()) {
        row = {
          ...row,
          plan_code: 'crece',
          display_name: 'Crece',
          max_branches: null,
          max_products: null,
          max_users: null,
          allow_employees: true,
          fiado_manual_only: true,
          fiado_payment_link: true,
          mp_qr_pos: true,
          mp_qr_demo_only: false,
          multi_branch: true,
          audit_enabled: true,
          fiscal_enabled: true,
          inteligencia_basica: true,
          inteligencia_avanzada: true,
          promotions_enabled: true,
          // Free mode: suppress trial/subscription UX (keep IAP/MP subscription code intact).
          subscription_status: null,
          subscription_expires_at: null,
          active_plan_grant: null,
          expired_signup_grant: null,
          show_signup_trial_expired_prompt: false,
          lapsed_subscription: null,
          show_subscription_lapsed_prompt: false,
          pending_subscription_plan: null,
        };
      }
      if (row) {
        setEntitlements(row);
        primeSessionOrganizationFromEntitlements(uid, row);
        setIsStale(false);
        if (row.organization_id) {
          setOnboardingCompleteFlag(true);
        }
        void clearPlanIntentIfSubscribed(row);
        try {
          await saveEntitlementsOffline(uid, row);
        } catch (persistErr) {
          if (!isDatabaseUnavailableError(persistErr)) {
            console.warn('[PlanEntitlementsProvider] cache write entitlements', persistErr);
          }
          setIsStale(true);
        }
      } else {
        await offlinePrime;
        if (!cachePrimed) {
          setEntitlements(null);
        } else {
          setIsStale(true);
        }
      }
    } catch (e) {
      if (useAuthStore.getState().session?.user?.id !== uid) return;
      if (!isDatabaseUnavailableError(e)) {
        console.warn('[PlanEntitlementsProvider] fetch', e);
      }
      await offlinePrime;
      if (!cachePrimed) {
        setEntitlements(null);
      }
      setIsStale(true);
    } finally {
      if (useAuthStore.getState().session?.user?.id === uid) {
        setLoading(false);
      }
    }
  }, [session?.user?.id]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refresh();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const kick = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        const row = entitlementsRef.current;
        const pending = String(row?.pending_subscription_plan ?? '').trim();
        const provider = String(row?.subscription_provider ?? '').trim().toLowerCase();
        const paid = provider !== '' && provider !== 'null';
        const orgId = (row?.organization_id as string | undefined) ?? null;
        const run = async () => {
          if (pending && !paid) await verifyMpSubscription(orgId);
          await refresh({ force: true });
        };
        void run();
        debounce = null;
      }, 800);
    };
    const appSub = AppState.addEventListener('change', (state) => {
      if (state === 'active') kick();
    });
    let removeVis = () => {};
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVis = () => {
        if (document.visibilityState === 'visible') kick();
      };
      document.addEventListener('visibilitychange', onVis);
      removeVis = () => document.removeEventListener('visibilitychange', onVis);
    }
    return () => {
      appSub.remove();
      removeVis();
      if (debounce) clearTimeout(debounce);
    };
  }, [refresh]);

  useEffect(() => {
    const expiresAt = entitlements?.subscription_expires_at;
    if (!entitlements?.subscription_provider || !expiresAt) return;
    const expiresMs = Date.parse(expiresAt);
    if (!Number.isFinite(expiresMs)) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const refreshAtExpiry = () => {
      const remaining = expiresMs - Date.now();
      if (remaining > 0) {
        timer = setTimeout(refreshAtExpiry, Math.min(remaining + 1000, 24 * 60 * 60 * 1000));
      } else {
        void refresh({ force: true });
      }
    };
    refreshAtExpiry();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [entitlements?.subscription_expires_at, entitlements?.subscription_provider, refresh]);

  const value = useMemo(
    () => ({
      entitlements,
      loading,
      refresh,
      isStale,
      onboardingCompleteFlag,
    }),
    [entitlements, loading, refresh, isStale, onboardingCompleteFlag]
  );

  return <PlanEntitlementsContext.Provider value={value}>{children}</PlanEntitlementsContext.Provider>;
}

export function usePlanEntitlements(): PlanEntitlementsContextValue {
  const ctx = useContext(PlanEntitlementsContext);
  if (!ctx) {
    throw new Error('usePlanEntitlements debe usarse dentro de PlanEntitlementsProvider');
  }
  return ctx;
}
